// src/pages/GoogleAdsSummary.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import {
  FiBarChart2,
  FiCalendar,
  FiRefreshCw,
  FiArrowLeft,
  FiAlertCircle,
} from "react-icons/fi";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";

import { adsApi } from "../lib/googleAds";

/* -------------------------
   Helpers / UI bits
-------------------------- */
const STATUS_META = {
  ENABLED: { label: "enabled", bg: "bg-green-100", text: "text-green-800", ring: "ring-green-200" },
  PAUSED: { label: "paused", bg: "bg-yellow-100", text: "text-yellow-800", ring: "ring-yellow-200" },
  REMOVED: { label: "removed", bg: "bg-red-100", text: "text-red-800", ring: "ring-red-200" },
  UNSPECIFIED: { label: "unspecified", bg: "bg-gray-100", text: "text-gray-700", ring: "ring-gray-200" },
  UNKNOWN: { label: "unknown", bg: "bg-gray-100", text: "text-gray-700", ring: "ring-gray-200" },
};

function Badge({ type, children }) {
  const meta = STATUS_META[type] || STATUS_META.UNKNOWN;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.bg} ${meta.text} ring-1 ${meta.ring}`}>
      {children}
    </span>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-900">{value}</div>
      {sub ? <div className="mt-1 text-xs text-gray-500">{sub}</div> : null}
    </div>
  );
}

function SkeletonBlock() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm animate-pulse">
      <div className="h-5 w-48 bg-gray-200 rounded mb-4" />
      <div className="h-72 w-full bg-gray-200 rounded" />
    </div>
  );
}

function lastNDaysRange(n = 15) {
  const end = new Date(); // today local
  const start = new Date();
  start.setDate(end.getDate() - (n - 1));
  const to = end.toISOString().slice(0, 10);
  const from = start.toISOString().slice(0, 10);
  return { from, to };
}

/* -------------------------
   Page
-------------------------- */
export default function GoogleAdsSummary() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Prefer campaign passed from the list page; otherwise use ?campaign_id=
  const stateCampaign = location.state?.campaign || null;
  const qpCampaignId = searchParams.get("campaign_id");
  const campaignId = stateCampaign?.id || qpCampaignId || "";

  // Campaign details (either from state or fetched)
  const [campaign, setCampaign] = useState(stateCampaign || null);
  const [loadingCampaign, setLoadingCampaign] = useState(!stateCampaign);
  const [errCampaign, setErrCampaign] = useState("");

  // Stats (selected period; default last 15 days)
  const defaultRange = useMemo(() => lastNDaysRange(15), []);
  const [dateFrom, setDateFrom] = useState(defaultRange.from);
  const [dateTo, setDateTo] = useState(defaultRange.to);
  const [rows, setRows] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [errStats, setErrStats] = useState("");

  // Action (pause/resume)
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const [actionErr, setActionErr] = useState("");

  // Currency (default INR until resolved via MCC children)
  const [currencyCode, setCurrencyCode] = useState("INR");
  const [currencyMsg, setCurrencyMsg] = useState("");

  // Totals for the selected period, alongside the per-day rows.
  const [totals, setTotals] = useState(null);

  const statsPath = useMemo(() => {
    if (!campaignId) return "";
    const params = new URLSearchParams({
      campaign_id: campaignId,
      date_from: dateFrom,
      date_to: dateTo,
      // The endpoint's parameter is `breakdown`. This page sent `daily=true`,
      // which FastAPI ignored, so no per-day rows ever came back.
      breakdown: "true",
    });
    return `/google/stats/campaign?${params.toString()}`;
  }, [campaignId, dateFrom, dateTo]);

  // Fetch campaign details if needed
  const fetchCampaignDetails = useCallback(async () => {
    if (campaign || !campaignId) return;
    setLoadingCampaign(true);
    setErrCampaign("");
    try {
      const data = await adsApi("/google/list_campaigns");
      const found = Array.isArray(data?.campaigns)
        ? data.campaigns.find((c) => String(c.id) === String(campaignId))
        : null;
      if (!found) throw new Error("That campaign is no longer in this account.");
      setCampaign(found);
    } catch (e) {
      setErrCampaign(e.message);
    } finally {
      setLoadingCampaign(false);
    }
  }, [campaign, campaignId]);

  // Fetch currency via list_children (manager → child)
  const fetchCurrency = useCallback(async () => {
    setCurrencyMsg("");
    try {
      const data = await adsApi("/google/list_children");
      const list = Array.isArray(data?.children) ? data.children : [];
      // Which child to read is the server's business — it is the account every
      // other endpoint operates on. This page used to carry the id as a
      // constant in the bundle, so pointing the stack at a different account
      // silently kept the old account's currency.
      const active = String(data?.active_customer_id || "");
      const child = list.find((c) => String(c.id) === active)
        || list.find((c) => c?.currency_code);

      if (child?.currency_code) {
        setCurrencyCode(child.currency_code);
      } else {
        setCurrencyCode("INR");
        setCurrencyMsg("Currency not reported by the account. Showing INR.");
      }
    } catch (e) {
      setCurrencyCode("INR");
      setCurrencyMsg(`Could not read the account currency (${e.message}). Showing INR.`);
    }
  }, []);


  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!statsPath) return;
    setLoadingStats(true);
    setErrStats("");
    try {
      const data = await adsApi(statsPath);
      // The per-day rows come back as `daily`; this page read `rows`, which the
      // endpoint uses for a count, so `Array.isArray` was always false and the
      // chart and table rendered empty even on a campaign with traffic.
      const arr = Array.isArray(data?.daily) ? [...data.daily] : [];
      arr.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
      setRows(arr);
      setTotals(data?.totals || null);
    } catch (e) {
      setErrStats(e.message);
      setRows([]);
      setTotals(null);
    } finally {
      setLoadingStats(false);
    }
  }, [statsPath]);

  useEffect(() => {
    fetchCampaignDetails();
  }, [fetchCampaignDetails]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchCurrency();
  }, [fetchCurrency]);

  // Toggle status (Resume/Pause)
  const toggleCampaignStatus = useCallback(async () => {
    if (!campaignId) return;
    setActionLoading(true);
    setActionMsg("");
    setActionErr("");

    const statusNow = (campaign?.status || "").toUpperCase();
    const isPaused = statusNow === "PAUSED";
    const path = isPaused ? "/google/enable_campaign" : "/google/pause_campaign";

    try {
      // Server takes the account and credentials from its own environment;
      // only the campaign is named here.
      const json = await adsApi(path, {
        method: "POST",
        body: JSON.stringify({ campaign_id: campaignId }),
      });
      if (json?.ok === false) throw new Error("Google Ads rejected the change.");

      const newStatus = isPaused ? "ENABLED" : "PAUSED";
      setCampaign((prev) => (prev ? { ...prev, status: newStatus } : prev));
      setActionMsg(isPaused ? "Campaign resumed." : "Campaign paused.");
      // Spend changes from here, so the figures on screen are now stale.
      fetchStats();
    } catch (e) {
      setActionErr(e.message);
    } finally {
      setActionLoading(false);
    }
  }, [campaign?.status, campaignId, fetchStats]);

  // Formatters using resolved currency
  const fmtNumber = useCallback((n) => {
    if (n === null || n === undefined) return "—";
    return Number(n).toLocaleString();
  }, []);

  const fmtMoney = useCallback(
    (n) => {
      if (n === null || n === undefined) return "—";
      try {
        return new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: currencyCode || "INR",
          maximumFractionDigits: 2,
        }).format(Number(n));
      } catch {
        return new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          maximumFractionDigits: 2,
        }).format(Number(n));
      }
    },
    [currencyCode]
  );

  // KPIs
  //
  // Prefer the server's own totals: they come from a separate un-segmented
  // query, so they include anything Google attributes to the period without
  // attaching it to a single day — summing the daily rows quietly under-counts
  // conversions, which are credited to the click's date, not the conversion's.
  const kpis = useMemo(() => {
    const summed = rows.reduce(
      (acc, r) => {
        acc.impressions += Number(r.impressions || 0);
        acc.clicks += Number(r.clicks || 0);
        acc.cost += Number(r.cost || 0);
        acc.conversions += Number(r.conversions || 0);
        acc.conversions_value += Number(r.conversions_value || 0);
        return acc;
      },
      { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversions_value: 0 }
    );

    const base = totals
      ? {
          impressions: Number(totals.impressions || 0),
          clicks: Number(totals.clicks || 0),
          cost: Number(totals.cost || 0),
          conversions: Number(totals.conversions || 0),
          conversions_value: Number(totals.conversions_value || 0),
        }
      : summed;

    // The server reports ctr as a ratio; this page shows a percentage.
    const ctr = base.impressions ? (base.clicks / base.impressions) * 100 : 0;
    const cpc = base.clicks ? base.cost / base.clicks : 0;
    const cpm = base.impressions ? (base.cost / base.impressions) * 1000 : 0;
    return { ...base, ctr, cpc, cpm };
  }, [rows, totals]);

  // Chart data (already sorted)
  const chartData = rows.map((r) => ({
    date: r.date,
    impressions: Number(r.impressions || 0),
    clicks: Number(r.clicks || 0),
    cost: Number(r.cost || 0),
    conversions: Number(r.conversions || 0),
  }));

  // Custom tooltip for better formatting
  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const pairs = payload.map((p) => {
      const name = p.name;
      const val = name?.toLowerCase().includes("cost")
        ? fmtMoney(p.value)
        : fmtNumber(p.value);
      return (
        <div key={p.dataKey} className="text-sm">
          <span className="font-medium">{name}: </span>
          <span>{val}</span>
        </div>
      );
    });
    return (
      <div className="rounded-lg border border-gray-200 bg-white/95 px-3 py-2 shadow-md">
        <div className="text-xs text-gray-500 mb-1">{label}</div>
        {pairs}
      </div>
    );
  };

  const title = campaign ? campaign.name : `Campaign ${campaignId || ""}`;
  const status = (campaign?.status || "UNKNOWN").toUpperCase();
  const isPaused = status === "PAUSED";
  const isEnabled = status === "ENABLED";

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        <div className="mx-auto" style={{ maxWidth: "1800px" }}>
          {/* Header */}
          <div className="mb-6 flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-start sm:gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100" title="Back">
                  <FiArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Google Ads — Summary</h1>
              </div>
              <p className="text-sm sm:text-base text-gray-600">Campaign details and performance for the selected period</p>
            </div>
          </div>

          {/* Campaign Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-xl font-semibold text-gray-900 truncate">{title}</h2>
                  {campaign && <Badge type={status}>{(status || "").toLowerCase()}</Badge>}

                  {(isPaused || isEnabled) && (
                    <button
                      onClick={toggleCampaignStatus}
                      disabled={actionLoading}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors text-sm ${
                        isPaused
                          ? "bg-green-600 text-white hover:bg-green-700 border-green-700"
                          : "bg-yellow-600 text-white hover:bg-yellow-700 border-yellow-700"
                      } ${actionLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                      title={isPaused ? "Resume this Ad" : "Pause this Ad"}
                    >
                      {actionLoading ? (
                        <span className="inline-block h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
                      ) : null}
                      <span>{isPaused ? "Resume this Ad" : "Pause this Ad"}</span>
                    </button>
                  )}
                </div>
                <div className="mt-1 text-sm text-gray-600">
                  Currency:&nbsp;<span className="font-medium">{currencyCode}</span>
                </div>

                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Campaign ID</div>
                    <div className="font-medium text-gray-900">{campaign?.id || campaignId || "—"}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Channel</div>
                    <div className="font-medium text-gray-900">{campaign?.channel || "—"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FiCalendar className="w-4 h-4 text-gray-500" />
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">{campaign?.start_date || "—"}</span>
                      <span className="mx-1 text-gray-400">–</span>
                      <span className="font-medium">{campaign?.end_date || "—"}</span>
                    </div>
                  </div>
                  <div className="col-span-1 sm:col-span-2 lg:col-span-3 text-xs text-gray-400 break-all">
                    {campaign?.resource_name}
                  </div>
                </div>
              </div>

              {/* Date range + refresh */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-gray-200"
                  />
                  <span className="text-gray-400">—</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-gray-200"
                  />
                </div>
                <button
                  onClick={fetchStats}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500 text-ink rounded-lg hover:bg-brand-600 transition-colors"
                >
                  <FiRefreshCw className="w-4 h-4" /> Refresh
                </button>
              </div>
            </div>

            {/* Campaign load error (if needed) */}
            {loadingCampaign && !campaign && (
              <div className="mt-3 text-sm text-gray-500">Loading campaign details…</div>
            )}
            {errCampaign && (
              <div className="mt-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                {errCampaign}
              </div>
            )}

            {/* Action feedback */}
            {actionMsg && (
              <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                {actionMsg}
              </div>
            )}
            {actionErr && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {actionErr}
              </div>
            )}

            {/* Currency fetch note (only show if we had to fall back) */}
            {currencyMsg && (
              <div className="mt-3 rounded-lg border border-brand-200 bg-brand-50 p-3 text-sm text-brand-800 flex items-start gap-2">
                <FiAlertCircle className="w-4 h-4 mt-0.5" />
                <span>{currencyMsg}</span>
              </div>
            )}
          </div>

          {/* KPI Row */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <StatCard label="Impressions" value={fmtNumber(kpis.impressions)} />
            <StatCard label="Clicks" value={fmtNumber(kpis.clicks)} sub={`CTR ${kpis.ctr.toFixed(2)}%`} />
            <StatCard label="Cost" value={fmtMoney(kpis.cost)} sub={`CPC ${fmtMoney(kpis.cpc)}`} />
            <StatCard label="CPM" value={fmtMoney(kpis.cpm)} />
            <StatCard label="Conversions" value={fmtNumber(kpis.conversions)} />
            <StatCard label="Conv. Value" value={fmtNumber(kpis.conversions_value)} />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Impressions & Clicks (bars) */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Daily Impressions & Clicks</h3>
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  <FiBarChart2 /> Selected period
                </div>
              </div>
              {loadingStats ? (
                <SkeletonBlock />
              ) : rows.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
                  <FiBarChart2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <div className="text-gray-700">No stats for the selected period.</div>
                </div>
              ) : (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend />
                      {/* Explicit colors as requested */}
                      <Bar yAxisId="left" dataKey="impressions" name="Impressions" fill="#F96168" />
                      <Bar yAxisId="right" dataKey="clicks" name="Clicks" fill="#000000" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Cost & Conversions (lines) */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Daily Cost & Conversions</h3>
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  <FiBarChart2 /> Selected period
                </div>
              </div>
              {loadingStats ? (
                <SkeletonBlock />
              ) : rows.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
                  <FiBarChart2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <div className="text-gray-700">No stats for the selected period.</div>
                </div>
              ) : (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="cost" name={`Cost (${currencyCode})`} dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="conversions" name="Conversions" dot />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Raw rows table */}
          <div className="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Breakdown</h3>
            {loadingStats ? (
              <SkeletonBlock />
            ) : rows.length === 0 ? (
              <div className="text-gray-600">No rows to display.</div>
            ) : (
              <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
                <table className="min-w-[640px] w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b">
                      <th className="py-2 pr-4">Date</th>
                      <th className="py-2 pr-4">Impressions</th>
                      <th className="py-2 pr-4">Clicks</th>
                      <th className="py-2 pr-4">Cost ({currencyCode})</th>
                      <th className="py-2 pr-4">Conversions</th>
                      <th className="py-2 pr-4">Conv. Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.date} className="border-b last:border-0">
                        <td className="py-2 pr-4">{r.date}</td>
                        <td className="py-2 pr-4">{fmtNumber(r.impressions)}</td>
                        <td className="py-2 pr-4">{fmtNumber(r.clicks)}</td>
                        <td className="py-2 pr-4">{fmtMoney(r.cost)}</td>
                        <td className="py-2 pr-4">{fmtNumber(r.conversions)}</td>
                        <td className="py-2 pr-4">{fmtNumber(r.conversions_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Stats error */}
            {errStats && (
              <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                {errStats}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
